import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from "react";
import classNames from "classnames";
import { Editor, Transforms, createEditor } from "slate";
import { Editable, ReactEditor, RenderElementProps, RenderLeafProps, Slate, withReact } from "slate-react";
import { withHistory } from "slate-history";
import { message } from "components";
import { UNSUPPORTED_PASTE_NOTICE } from "./configs";
import { PROMPT_VARIABLE_CLASS, PromptVariableRules, promptVariableToken } from "./variables";
import { isKnownVariable, markdownToPromptBlocks, promptBlocksToMarkdown } from "./markdown";
import {
  insertVariable,
  promptDocumentBlocks,
  resetPromptDocument,
  withMarkdownPaste,
  withMarkdownShortcuts,
  withPromptVariables,
} from "./slate";
import PromptEditorToolbar from "./Toolbar";
import styles from "./PromptEditor.module.scss";

export interface PromptEditorHandle {
  insertVariable: (name: string) => void;
  focus: () => void;
}

interface Props {
  value: string;
  rules: PromptVariableRules;
  readOnly?: boolean;
  /**
   * 🔴 FOR AN EDITOR INSIDE SOMETHING THAT ALREADY SCROLLS — the compare dialog's panes. The workspace's copy
   * is pinned to a slot and takes its height from the column, which is the opposite arrangement; leaving both
   * on would put a scrollbar inside a scrollbar.
   */
  autoHeight?: boolean;
  /** 🔴 Absent for a read-only copy, which has no change to report — the compare dialog renders one. */
  onChange?: (markdown: string) => void;
}

const PromptEditor = forwardRef<PromptEditorHandle, Props>(({ value, rules, readOnly, autoHeight, onChange }, ref) => {
  /**
   * 🔴 THE ORDER IS THE PLUGIN ORDER. `withMarkdownPaste` and `withMarkdownShortcuts` wrap the editor that
   * already knows about variables, so a pasted or typed `{{name}}` is normalised into a chip by the layer
   * underneath them rather than each of them having to know about chips.
   */
  const editor = useMemo(
    () =>
      withMarkdownShortcuts(
        withMarkdownPaste(withPromptVariables(withHistory(withReact(createEditor()))), (unsupported) =>
          message.warning(UNSUPPORTED_PASTE_NOTICE(unsupported))
        )
      ),
    []
  );
  const lastEmittedRef = useRef(value);
  const initialValue = useMemo(() => markdownToPromptBlocks(value).blocks, []);

  useEffect(() => {
    if (value !== lastEmittedRef.current) {
      lastEmittedRef.current = value;
      resetPromptDocument(editor, markdownToPromptBlocks(value).blocks);
    }
  }, [editor, value]);

  const focusEditor = useCallback(() => {
    if (!editor.selection) {
      Transforms.select(editor, Editor.end(editor, []));
    }
    ReactEditor.focus(editor);
  }, [editor]);

  useImperativeHandle(ref, () => ({
    insertVariable: (name: string) => {
      insertVariable(editor, name);
      /**
       * 🔴 THE CARET HAS TO BE PUT BACK. The dictionary's button prevents its own mousedown so the focus
       * never leaves, but a keyboard activation — or any caller that reaches this handle another way — does
       * lose it, and a chip arriving in an editor with no caret reads as "it stopped working".
       */
      focusEditor();
    },
    focus: focusEditor,
  }));

  const renderElement = useCallback((props: RenderElementProps) => <PromptElement {...props} rules={rules} />, [rules]);

  return (
    <div className={classNames(styles.editor, { [styles.editorAuto]: autoHeight })}>
      <Slate
        editor={editor}
        initialValue={initialValue}
        onChange={() => {
          const markdown = promptBlocksToMarkdown(promptDocumentBlocks(editor));
          lastEmittedRef.current = markdown;
          onChange?.(markdown);
        }}
      >
        {!readOnly && <PromptEditorToolbar />}
        <Editable
          className={classNames(styles.editorSurface, { [styles.editorSurfaceAuto]: autoHeight })}
          readOnly={readOnly}
          renderElement={renderElement}
          renderLeaf={PromptLeaf}
        />
      </Slate>
    </div>
  );
});

function PromptLeaf({ attributes, children, leaf }: RenderLeafProps) {
  return (
    <span
      {...attributes}
      style={{
        fontWeight: leaf.bold ? 600 : undefined,
        fontStyle: leaf.italic ? "italic" : undefined,
        textDecoration: leaf.underline ? "underline" : undefined,
      }}
    >
      {children}
    </span>
  );
}

function PromptElement({ attributes, children, element, rules }: RenderElementProps & { rules: PromptVariableRules }) {
  switch (element.type) {
    case "variable":
      return (
        <span
          {...attributes}
          className={PROMPT_VARIABLE_CLASS}
          data-name={element.name}
          data-required={String(!!rules.requiredNames?.includes(element.name))}
          data-known={String(isKnownVariable(element.name, rules))}
          contentEditable={false}
        >
          {promptVariableToken(element.name)}
          {children}
        </span>
      );
    case "link":
      return (
        <a {...attributes} href={element.url} className={styles.editorLink} title={element.url}>
          {children}
        </a>
      );
    case "heading":
      return React.createElement(`h${element.level}`, { ...attributes, className: styles.editorHeading }, children);
    case "bulleted-list":
      return <ul {...attributes}>{children}</ul>;
    case "numbered-list":
      return <ol {...attributes}>{children}</ol>;
    case "list-item":
      return <li {...attributes}>{children}</li>;
    default:
      return <p {...attributes}>{children}</p>;
  }
}

export default PromptEditor;
